import { api } from "boot/axios";
import QuickLRU from "quick-lru";
import { throttle } from "lodash";
import memoize from "memoizee";

const growthAbove40PercentCache = new QuickLRU({
  maxSize: 100,
  maxAge: 60 * 1000,
});
const tradingBelowFairCache = new QuickLRU({
  maxSize: 100,
  maxAge: 60 * 1000,
});
const stockReportCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const sectorDimensionCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const sectorDetailCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const sectorReportCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const lowCapCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const fundamentalScanProCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});
const fundamentalScanCache = new QuickLRU({
  maxSize: 500,
  maxAge: 60 * 1000,
});

let cachedStockData = null;
let cachedStockPromise = null;
let cachedIndexData = null;
let cachedSectorData = null;
let cachedBrokerData = null;
let cachedBrokerPromise = null;
let bonusPromise = {
  cSymbol: {},
  timestamps: {},
};

export default () => {
  // Float Cap
  const floatCap = async (symbol) => {
    try {
      const url = symbol
        ? `/data/market-cap-category/?symbol=${encodeURIComponent(symbol)}`
        : `/data/market-cap-category/`;
      const data = await api.get(url);
      return data;
    } catch (error) {
      throw error;
    }
  };
  //All Stock
  const allStock = async () => {
    if (cachedStockData) {
      return cachedStockData;
    }

    if (cachedStockPromise) {
      return cachedStockPromise;
    }

    try {
      // Fetch data from the API
      cachedStockPromise = api.get("/stock/");
      const data = await cachedStockPromise;
      cachedStockData = data;
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //All Index
  const allIndex = async () => {
    if (cachedIndexData) {
      return cachedIndexData;
    }
    try {
      const data = await api.get("/index/");
      cachedIndexData = data;
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //All Sector
  const allSector = throttle(async () => {
    if (cachedSectorData) {
      return cachedSectorData;
    }
    try {
      const data = await api.get("/sector/");
      cachedSectorData = data;
      return data;
    } catch (error) {
      throw new Error(error);
    }
  }, 3000);

  //All Sector

  const sectorDetail = async (id) => {
    if (sectorDetailCache.has(id)) {
      return { data: sectorDetailCache.get(id) };
    }
    try {
      const { data } = await api.get(`/sector/${id}/`);
      sectorDetailCache.set(id, data);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  };

  //Sector Dimensions
  const sectorDimension = async (id) => {
    if (sectorDimensionCache.has(id)) {
      return sectorDimensionCache.get(id);
    }
    try {
      const data = await api.get("/sector/" + id + "/dimensions/");
      sectorDimensionCache.set(id, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Sector Report
  // Sector Report

  const sectorReport = async (id) => {
    if (sectorReportCache.has(id)) {
      return { data: sectorReportCache.get(id) };
    }

    try {
      const { data } = await api.get(`/sector/${id}/report/`);
      sectorReportCache.set(id, data);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  };

  //growth greater 40%
  const growthAbove40Percent = async (id) => {
    if (growthAbove40PercentCache.has(id)) {
      return growthAbove40PercentCache.get(id);
    }
    try {
      const data = await api.get("/sector/" + id + "/growth-above-40/");
      growthAbove40PercentCache.set(id, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };
  //trading below fair value
  const tradingBelowFair = async (id) => {
    if (tradingBelowFairCache.has(id)) {
      return tradingBelowFairCache.get(id);
    }
    try {
      const data = await api.get("/sector/" + id + "/trading-below-fair/");
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };
  //trading below fair value
  const lowCapCompany = async () => {
    if (lowCapCache.has("res")) {
      return lowCapCache.get("res");
    }
    try {
      const data = await api.get("/sector/low-cap/");
      lowCapCache.set("res", data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Stock Search

  const stockSearchCache = new Map();
  const stockSearch = async (symbol) => {
    if (stockSearchCache.has(symbol)) {
      return stockSearchCache.get(symbol);
    }

    try {
      const res = await api.get(`/stock/?search=${symbol}`);
      const stock = res.data.filter((stock) => stock.symbol === symbol)[0];
      stockSearchCache.set(symbol, stock);
      return stock;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Stock Report

  const stockReport = memoize(
    async (id) => {
      if (stockReportCache.has(id)) {
        return stockReportCache.get(id);
      }
      try {
        const data = await api.get("/stock/" + id + "/report/");
        stockReportCache.set(id, data);
        return data;
      } catch (error) {
        throw new Error(error);
      }
    },
    {
      maxAge: 60 * 1000,
      promise: true,
      normalizer: (args) => JSON.stringify(args),
    }
  );

  //Bonus
  const bonus = async (symbol) => {
    const now = Date.now();
    const cacheTime = 5000;
    const cacheKey = symbol || "all";

    if (
      bonusPromise.cSymbol[cacheKey] &&
      now - bonusPromise.timestamps[cacheKey] < cacheTime
    ) {
      return bonusPromise.cSymbol[cacheKey];
    }

    try {
      bonusPromise.timestamps[cacheKey] = now;
      bonusPromise.cSymbol[cacheKey] = api.get("/bonus/", {
        params: { symbol: symbol },
      });

      const data = await bonusPromise.cSymbol[cacheKey];
      return data;
    } catch (error) {
      bonusPromise.cSymbol[cacheKey] = null;
      throw new Error(error);
    }
  };

  //Bonus
  const right = async (symbol) => {
    try {
      const data = await api.get("/right/", {
        params: {
          symbol: symbol,
        },
      });
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Broker list
  const brokerList = async () => {
    if (cachedBrokerData) {
      return cachedBrokerData;
    }
    if (cachedBrokerPromise) {
      return cachedBrokerPromise;
    }
    try {
      cachedBrokerPromise = api.get("/broker/");
      const data = await cachedBrokerPromise;
      cachedBrokerPromise = null;
      cachedBrokerData = data;
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Fundamental Scan
  const fundamentalScan = async () => {
    let cacheKey = "respose";
    if (fundamentalScanCache.has(cacheKey)) {
      return fundamentalScanCache.get(cacheKey);
    }
    try {
      const data = await api.get("/fundamental-scan/");
      fundamentalScanCache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //Fundamental Scan Pro
  const fundamentalScanPro = async ({ fiscal_year = null, quarter = null }) => {
    let cacheKey = `${fiscal_year}-${quarter}`;
    if (fundamentalScanProCache.has(cacheKey)) {
      return fundamentalScanProCache.get(cacheKey);
    }
    try {
      const data = await api.get(
        `/fundamental-scan-pro/?fiscal_year=${fiscal_year}&quarter=${quarter}`
      );
      fundamentalScanProCache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const clearCoreCache = () => {
    cachedStockData = null;
    cachedIndexData = null;
    cachedSectorData = null;
  };

  return {
    floatCap,
    fundamentalScan,
    fundamentalScanPro,
    allStock,
    stockSearch,
    stockReport,

    allSector,
    sectorDetail,
    sectorDimension,
    sectorReport,
    growthAbove40Percent,
    tradingBelowFair,
    lowCapCompany,

    allIndex,

    brokerList,

    bonus,
    right,
    clearCoreCache,
  };
};
